/**
 * <rz-recommendations data-url>: loads Shopify product recommendations when the element
 * scrolls near the viewport. The URL renders a section; the matching [data-recommendations]
 * element from the response replaces this one's content (empty result = element removed).
 *
 * <rz-recently-viewed data-section-id data-limit data-exclude>: renders the products stored
 * by product pages in localStorage (newest first) via a search for their ids.
 */
// Wrapped: several sections include this file on one page; top-level consts would be declared twice.
(() => {
  const RZ_RECENT_KEY = 'rz-recently-viewed';

  const rzReadRecent = (key = RZ_RECENT_KEY) => {
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (e) {
      return [];
    }
  };

  const rzLoadWhenNear = (element, load) => {
    // Inside a drawer (<dialog>) the section never scrolls into view: load when the dialog opens.
    const dialog = element.closest('dialog');
    if (dialog) {
      if (dialog.open) return load();
      const watcher = new MutationObserver(() => {
        if (dialog.open) {
          watcher.disconnect();
          load();
        }
      });
      watcher.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '600px 0px' }
    );
    // The element itself may still be hidden (display:none never intersects); watch its section.
    observer.observe(element.closest('.shopify-section') || element.parentElement || element);
  };

  const rzSwapFrom = async (element, url) => {
    const html = await fetch(url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`[data-recommendations="${element.dataset.recommendations}"]`);
    if (!fresh || !fresh.querySelector('li')) {
      element.closest('[data-hide-when-empty]')?.setAttribute('hidden', '');
      element.remove();
      return;
    }
    element.innerHTML = fresh.innerHTML;
    element.closest('[data-hide-when-empty]')?.removeAttribute('hidden');
  };

  if (!customElements.get('rz-recommendations')) {
    customElements.define(
      'rz-recommendations',
      class extends HTMLElement {
        connectedCallback() {
          rzLoadWhenNear(this, () => rzSwapFrom(this, this.dataset.url));
        }
      }
    );
  }

  if (!customElements.get('rz-recently-viewed')) {
    customElements.define(
      'rz-recently-viewed',
      class extends HTMLElement {
        connectedCallback() {
          const exclude = Number(this.dataset.exclude || 0);
          // data-key="rz-wishlist" reuses this element for the wishlist page.
          const ids = rzReadRecent(this.dataset.key)
            .filter((id) => id !== exclude)
            .slice(0, Number(this.dataset.limit || 8));
          if (!ids.length) {
            this.closest('[data-hide-when-empty]')?.setAttribute('hidden', '');
            return;
          }
          const q = ids.map((id) => `id:${id}`).join(' OR ');
          const root = window.Shopify?.routes?.root || '/';
          const url = `${root}search?type=product&q=${encodeURIComponent(q)}&section_id=${this.dataset.sectionId}`;
          rzLoadWhenNear(this, async () => {
            await rzSwapFrom(this, url);
            // Search returns relevance order; show newest-viewed first.
            const track = this.querySelector('[data-track]');
            ids.forEach((id) => {
              const item = track?.querySelector(`li[data-id="${id}"]`);
              if (item) track.append(item);
            });
          });
        }
      }
    );
  }
})();
